// PRD.283 — Clinic Main Module Enable / Disable
//
// Clinic-level main-module configuration is stored under
// `app_config.extra_json.main_modules`, mirroring the established
// `regional_settings` / `clinic_classification` patterns (see app-config.ts and
// clinic-classification.ts). It rides the existing PowerSync + SyncWrite
// `extra_json` path, so no schema/migration or write-path changes are required.
//
// First phase: only the `invoice` module is configurable. Every other main
// module is locked-on in the UI and never persists a clinic override. The shape
// is intentionally small but keyed by module name so future modules can be
// added by extending `ClinicMainModuleKey` and the config type — no framework.
//
// Backward compatibility (PRD §Backward Compatibility): a MISSING value means
// ENABLED. Only an explicit `false` disables a module. This lets every existing
// clinic keep Invoice without a migration. Reads never throw: malformed JSON or
// a missing `main_modules` object safely falls back to enabled.
//
// This module is intentionally self-contained (no workspace-package imports) so
// it can run under the node:test harness. The `app_config` row is accepted via
// a structural shape that AppConfigRow satisfies.

// ─── Module keys ───

/** Configurable main-module keys. Extend this union to add future modules. */
export type ClinicMainModuleKey = 'invoice';

/**
 * Persisted clinic main-module overrides. Each key is optional; a missing key
 * means "enabled" (see `isClinicModuleEnabled`). Only stored keys are written,
 * so the JSON stays minimal and forward-compatible.
 */
export interface ClinicMainModuleConfig {
  invoice?: boolean;
}

// ─── Read / normalize ───

/** Structural shape of the source row — AppConfigRow satisfies this. */
export interface MainModuleConfigSource {
  extra_json?: string | null;
}

/**
 * Parse `extra_json` (text from PowerSync SQLite, object from server) into a
 * plain object. Falls back to {} on any malformed input. Kept local so this
 * module stays free of workspace-package imports (node:test compatibility).
 */
function parseExtraJson(extraJson: unknown): Record<string, unknown> {
  if (extraJson == null) return {};
  if (typeof extraJson === 'object' && !Array.isArray(extraJson)) {
    return extraJson as Record<string, unknown>;
  }
  if (typeof extraJson === 'string' && extraJson.trim().length > 0) {
    try {
      const parsed = JSON.parse(extraJson);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* fall through */
    }
  }
  return {};
}

/**
 * Normalize an arbitrary/untrusted `main_modules` value into a canonical
 * `ClinicMainModuleConfig`. Only recognized boolean keys are kept; anything
 * else is dropped. Pure and deterministic — safe on read and before write.
 */
export function normalizeMainModuleConfig(raw: unknown): ClinicMainModuleConfig {
  const obj =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const config: ClinicMainModuleConfig = {};
  if (typeof obj.invoice === 'boolean') config.invoice = obj.invoice;
  return config;
}

/**
 * Extract the clinic main-module config from an app_config row. Never throws;
 * malformed/missing `extra_json` yields an empty config (everything enabled).
 */
export function getMainModuleConfig(
  row: MainModuleConfigSource | null | undefined,
): ClinicMainModuleConfig {
  const extra = parseExtraJson(row?.extra_json);
  return normalizeMainModuleConfig(extra.main_modules);
}

/**
 * Whether a main module is enabled for the clinic. A missing value means
 * enabled — only an explicit `false` disables it (PRD §Backward Compatibility).
 * This is a RESTRICTIVE-ONLY clinic preference; platform feature flags and user
 * permissions remain authoritative above it.
 */
export function isClinicModuleEnabled(
  config: ClinicMainModuleConfig | null | undefined,
  module: ClinicMainModuleKey,
): boolean {
  return config?.[module] !== false;
}

/** Convenience: read the Invoice module state directly from an app_config row. */
export function isInvoiceModuleEnabled(row: MainModuleConfigSource | null | undefined): boolean {
  return isClinicModuleEnabled(getMainModuleConfig(row), 'invoice');
}

// ─── Write ───

/**
 * Serialize a partial main-module change into an updated `extra_json` JSON
 * string, preserving every other top-level key (regional_settings,
 * clinic_classification, unknown future keys) AND every other main_modules key.
 * Only the provided keys are replaced.
 */
export function writeMainModules(
  currentExtraJson: unknown,
  changes: ClinicMainModuleConfig,
): string {
  const next = { ...parseExtraJson(currentExtraJson) };
  const currentModules = normalizeMainModuleConfig(next.main_modules);
  next.main_modules = { ...currentModules, ...normalizeMainModuleConfig(changes) };
  return JSON.stringify(next);
}
